import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, PhoneIcon, XIcon } from 'lucide-react';
import { business } from '../data/business';
import type { Service, ServiceOption } from '../types/content';
interface ServiceDrawerProps {
  service: Service | null;
  onClose: () => void;
  onRequestEstimate: (service: ServiceOption) => void;
}
export function ServiceDrawer({
  service,
  onClose,
  onRequestEstimate
}: ServiceDrawerProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!service) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [service, onClose]);
  return <AnimatePresence data-id="element-274" data-mp="373t3cgrnxm6t">
      {service && <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-labelledby="service-drawer-title" data-id="element-275" data-mp="1cdp43sfzehwm">
          <motion.div className="absolute inset-0 bg-navy-900/50" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} onClick={onClose} data-id="element-276" data-mp="1hb8vlu1owr6i" />
          <motion.aside className="relative flex h-full w-full max-w-lg flex-col overflow-y-auto bg-white" initial={{
        x: 40,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 40,
        opacity: 0
      }} transition={{
        duration: 0.26,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-277" data-mp="tfcs7yxoqtxc">
            <div className="relative aspect-[16/10] shrink-0" data-id="element-278" data-mp="17fnnrf9xwwou">
              <img src={service.image} alt={service.imageAlt} className="h-full w-full object-cover" data-id="element-279" data-mp="2dv7qq6n2uiuo" />
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white text-navy shadow-md transition-colors duration-150 hover:bg-mist focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cyan" data-id="element-280" data-mp="2isq1m2knn85b">
                <XIcon className="h-5 w-5" data-id="element-281" data-mp="nqw5r8ptsh5e" />
              </button>
            </div>
            <div className="flex flex-1 flex-col p-6 md:p-8" data-id="element-282" data-mp="1tow330msby">
              <h3 id="service-drawer-title" className="font-display text-3xl font-extrabold tracking-tight text-navy" data-id="element-283" data-mp="3r7czt3x76nke">
                {service.title}
              </h3>
              <p className="mt-3 text-lg leading-relaxed text-navy/70" data-id="element-284" data-mp="9socbarzzxyz">{service.description}</p>

              <h4 className="mt-8 text-sm font-semibold text-navy" data-id="element-285" data-mp="4xs87c9hlk5i">What's typically included</h4>
              <ul className="mt-4 space-y-3 border-t border-line pt-4" data-id="element-286" data-mp="3ddtzndgdrqv4">
                {service.details.map(d => <li key={d} className="flex items-start gap-3 text-[15px] text-navy/80" data-id="element-287" data-mp="1nyjdxjx8vg72">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-aqua" aria-hidden="true" data-id="element-288" data-mp="1nwq5q06zgnwy" />
                    {d}
                  </li>)}
              </ul>
              <p className="mt-6 text-sm text-navy/60" data-id="element-289" data-mp="1ezpk21bat3x7">
                Every pool is different. We'll confirm the exact scope during your free estimate.
              </p>

              <div className="mt-auto grid gap-3 pt-8" data-id="element-290" data-mp="26q3qjcgzfdyu">
                <button type="button" onClick={() => onRequestEstimate(service.formOption)} className="btn-primary w-full" data-id="element-291" data-mp="2bo59lxeerdab">
                  Get Your Free Estimate
                </button>
                <a href={business.phoneHref} className="btn-outline w-full" data-id="element-292" data-mp="1xqp1mhggrbgg">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-293" data-mp="22lmczv17twwi" />
                  Call Us {business.phoneDisplay}
                </a>
              </div>
            </div>
          </motion.aside>
        </div>}
    </AnimatePresence>;
}